const DEFAULT_LOCALE = undefined; // Falls back to browser locale

function getLocale(locale) {
  return locale || DEFAULT_LOCALE;
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date string to a localized date (e.g., "Feb 16, 2026")
 */
export function formatDate(dateString, locale) {
  const date = toDate(dateString);
  if (!date) return '';
  return new Intl.DateTimeFormat(getLocale(locale), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

/**
 * Format a date string to a short localized date (e.g., "Feb 16, 2026")
 */
export function formatShortDate(dateString, locale) {
  const date = toDate(dateString);
  if (!date) return '';
  return new Intl.DateTimeFormat(getLocale(locale), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Format a date string to a localized 12-hour time (e.g., "2:30 PM")
 */
export function formatTime(dateString, locale) {
  const date = toDate(dateString);
  if (!date) return '';
  return new Intl.DateTimeFormat(getLocale(locale), {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/**
 * Format a date string to localized date and time (e.g., "Feb 16, 2026 at 2:30 PM")
 */
export function formatDateTime(dateString, locale) {
  const date = toDate(dateString);
  if (!date) return '';
  return new Intl.DateTimeFormat(getLocale(locale), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/**
 * Format to short date + time (e.g., "Feb 16, 2:30 PM")
 */
export function formatShortDateTime(dateString, locale) {
  const date = toDate(dateString);
  if (!date) return '';
  return new Intl.DateTimeFormat(getLocale(locale), {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/**
 * Format to time only, compact (e.g., "2:30 PM")
 * Used in order cards/columns for quick time display.
 */
export function formatCompactTime(dateString, locale) {
  return formatTime(dateString, locale);
}

/**
 * Format a date to date-only for display (e.g., "Feb 16, 2026")
 * Used in subscription pages, member joined dates, etc.
 */
export function formatDateOnly(dateString, locale) {
  return formatShortDate(dateString, locale);
}

/**
 * Format a raw time string (HH:mm or HH:mm:ss) to 12-hour display.
 * Useful for business hours or time inputs that come as strings.
 */
export function formatTimeString(timeStr) {
  if (!timeStr) return '';
  const parts = timeStr.split(':');
  const hour = parseInt(parts[0], 10);
  const minute = parts[1] || '00';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${ampm}`;
}

/**
 * Check if a date string represents today.
 */
export function isToday(dateString) {
  const date = toDate(dateString);
  if (!date) return false;
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

/**
 * Check if a date string represents yesterday.
 */
export function isYesterday(dateString) {
  const date = toDate(dateString);
  if (!date) return false;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate()
  );
}
